import { IUser } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useUserQueries } from '../../../../../queries';
import { useUserAutocompleteQuery } from '../../../../../queries/User/useUserAutocompleteQuery';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
	ComboboxValue,
} from '../../../../../web/components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { bs } from '../../../../../web/styles/styles';
import { styleSheet } from '../styles';

interface IProps {
	users?: IUser[];
	onChange: (users: IUser[]) => void;
}

export const UserMultiSelect: React.FC<IProps> = ({ users: selectedUsers = [], onChange }) => {
	const id = React.useId();
	const [searchValue, setSearchValue] = useState('');
	const userAutocompleteQuery = useUserAutocompleteQuery({
		fragment: searchValue,
		enabled: Boolean(searchValue),
	});
	const comboboxItems = userAutocompleteQuery.data?.values || [];
	const userQueries = useUserQueries({
		ids: selectedUsers.map(user => user.id),
	});
	const comboboxValue = userQueries.map(query => query.data).filter(Boolean);
	const handleUserSelect = (users: IUser[]) => {
		onChange([...new Map(users.map(user => [user.id, user])).values()]);
	};
	let status: React.ReactNode = '';
	if (userAutocompleteQuery.isLoading) {
		status = (
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
				<LoadingSpinner type='extra-tiny' />
				Searching SDRs...
			</div>
		);
	} else if (userAutocompleteQuery.error) {
		status = userAutocompleteQuery.error?.message;
	} else if (userAutocompleteQuery.data?.values?.length === 0 && searchValue) {
		status = `No SDRs found for "${searchValue}"`;
	} else if (userAutocompleteQuery.data?.values?.length > 0) {
		status = `${userAutocompleteQuery.data.values.length} SDR${userAutocompleteQuery.data.values.length === 1 ? '' : 's'} found`;
	} else if (!searchValue) {
		status = 'Start typing to search SDRs...';
	}

	return (
		<Combobox
			multiple
			items={comboboxItems}
			value={comboboxValue}
			onValueChange={value => handleUserSelect(value as IUser[])}
			inputValue={searchValue}
			onInputValueChange={setSearchValue}
			isItemEqualToValue={(item: IUser, value: IUser) => {
				return item.id === value.id;
			}}
			itemToStringValue={(sdr: IUser) => sdr.id}
		>
			<div className={css(bs.flex, bs.flexCol, bs.gap1)}>
				<label htmlFor={id} className={css(bs.textXs, bs.fontBold)}>
					SDR
				</label>
				<ComboboxInput placeholder='Search and select SDRs...' id={id} showClear />
				<ComboboxChips styles={[styleSheet.chips]}>
					<ComboboxValue>
						{(sdrs: IUser[]) => (
							<React.Fragment>
								{sdrs.map(sdr => (
									<ComboboxChip key={sdr.id} aria-label={getDisplayName(sdr)}>
										{getDisplayName(sdr)}
										<ComboboxChipRemove />
									</ComboboxChip>
								))}
							</React.Fragment>
						)}
					</ComboboxValue>
				</ComboboxChips>
			</div>
			<ComboboxContent>
				<ComboboxStatus>{status}</ComboboxStatus>
				<ComboboxList>
					{(user: IUser) => (
						<ComboboxAvatarItem key={user.id} value={user}>
							<ComboboxAvatarItemAvatar
								imageUrl={user.profilePic}
								fallbackText={user.firstName?.substring(0, 1) + user.lastName?.substring(0, 1)}
							/>
							<ComboboxAvatarItemTitle>{getDisplayName(user)}</ComboboxAvatarItemTitle>
							<ComboboxAvatarItemDescription>{user.email}</ComboboxAvatarItemDescription>
						</ComboboxAvatarItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};
